// Chiamate alle rotte di Better Auth che il server espone (`apps/server/src/auth-routes.ts`), con
// `fetch` e senza il client di Better Auth. La SPA e l'API hanno la stessa origine: il cookie di
// sessione viaggia da sé e il controllo dell'origine di Better Auth riconosce l'indirizzo pubblico.
// Password e token del link stanno soltanto nel corpo JSON delle richieste: mai nell'indirizzo, nei
// log o nei messaggi d'errore. La sessione si legge soltanto da `GET /api/me` (`meQuery` in `queries.ts`).

/** Come `minPasswordLength` in `apps/server/src/auth.ts`: le due regole vanno cambiate insieme. */
export const PASSWORD_MIN_LENGTH = 12;
/** Il massimo predefinito di Better Auth (`maxPasswordLength`), che il server non cambia. */
export const PASSWORD_MAX_LENGTH = 128;

/** Perché una richiesta non è riuscita, nei soli termini che l'interfaccia sa spiegare. */
export type AuthFailure =
  | "invalid_credentials"
  | "invalid_link"
  | "wrong_password"
  | "password_too_short"
  | "password_too_long"
  | "session_expired"
  | "rate_limited"
  | "unavailable"
  | "unexpected";

export type AuthResult = { ok: true } | { ok: false; failure: AuthFailure };

/** La pagina di Google dove il browser va a scegliere l'account (AZI-11): nessun altro indirizzo. */
const GOOGLE_ACCOUNTS_ORIGIN = "https://accounts.google.com";

/**
 * Il browser lascia Gioia365 per la pagina di Google (AZI-11). A parte, così i test la sostituiscono: jsdom non naviga fuori
 * dalla pagina.
 */
export const browserNavigation = {
  leaveFor(url: string): void {
    window.location.assign(url);
  },
};

const AUTH_BASE = "/api/auth";

async function post(path: string, body: unknown): Promise<Response | null> {
  try {
    return await fetch(`${AUTH_BASE}${path}`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    // Rete assente o server spento: `fetch` rifiuta senza una risposta da leggere.
    return null;
  }
}

async function errorCodeOf(response: Response): Promise<string | undefined> {
  try {
    const body: unknown = await response.json();
    if (typeof body === "object" && body !== null && "code" in body && typeof body.code === "string") return body.code;
  } catch {
    /* corpo assente o non JSON: conta soltanto lo stato HTTP */
  }
  return undefined;
}

/** Stati comuni a tutte le rotte; `undefined` lascia la decisione alla rotta chiamante. */
function commonFailure(response: Response | null): AuthFailure | undefined {
  if (response === null) return "unavailable";
  if (response.status === 429) return "rate_limited";
  // 404: il server è partito senza database di piattaforma e non monta l'autenticazione.
  // 502–504: il proxy dell'hosting non raggiunge il server.
  if ([404, 502, 503, 504].includes(response.status)) return "unavailable";
  return undefined;
}

export const authApi = {
  async signIn(input: { email: string; password: string }): Promise<AuthResult> {
    const response = await post("/sign-in/email", { email: input.email.trim(), password: input.password });
    if (response?.ok) return { ok: true };
    const common = commonFailure(response);
    if (common) return { ok: false, failure: common };
    // 401 per email sconosciuta o password sbagliata, senza distinguere; 400 per un'email malformata.
    if (response?.status === 401 || response?.status === 400) return { ok: false, failure: "invalid_credentials" };
    return { ok: false, failure: "unexpected" };
  },

  /**
   * «Accedi con Google» (AZI-11): il server prepara l'accesso (lo stato che lega il ritorno a questo browser) e risponde con la
   * pagina di Google, dove il browser va da sé. I due ritorni sono la pagina di accesso: `callbackURL` dopo un accesso riuscito,
   * `errorCallbackURL` con il motivo di un rifiuto. Il server accetta soltanto questi.
   */
  async startGoogleSignIn(input: { callbackURL: string; errorCallbackURL: string }): Promise<{ ok: true; url: string } | { ok: false; failure: AuthFailure }> {
    const response = await post("/sign-in/social", { provider: "google", callbackURL: input.callbackURL, errorCallbackURL: input.errorCallbackURL, disableRedirect: true });
    const common = commonFailure(response);
    if (common) return { ok: false, failure: common };
    if (!response?.ok) return { ok: false, failure: "unexpected" };
    try {
      const body: unknown = await response.json();
      const url = typeof body === "object" && body !== null && "url" in body && typeof body.url === "string" ? new URL(body.url) : null;
      if (url?.origin === GOOGLE_ACCOUNTS_ORIGIN) return { ok: true, url: url.href };
    } catch {
      /* né JSON né un indirizzo: come una risposta inattesa */
    }
    return { ok: false, failure: "unexpected" };
  },

  async signOut(): Promise<AuthResult> {
    const response = await post("/sign-out", {});
    if (response?.ok) return { ok: true };
    return { ok: false, failure: commonFailure(response) ?? "unexpected" };
  },

  /**
   * «Password dimenticata?»: Better Auth risponde allo stesso modo per un'email che ha un account e per una che
   * non lo ha, e il server manda l'email dopo la risposta (piano M1b D10); l'interfaccia non distingue nulla.
   */
  async requestPasswordReset(input: { email: string }): Promise<AuthResult> {
    const response = await post("/request-password-reset", { email: input.email.trim() });
    if (response?.ok) return { ok: true };
    return { ok: false, failure: commonFailure(response) ?? "unexpected" };
  },

  async resetPassword(input: { token: string; newPassword: string }): Promise<AuthResult> {
    const response = await post("/reset-password", { newPassword: input.newPassword, token: input.token });
    if (response?.ok) return { ok: true };
    const common = commonFailure(response);
    if (common) return { ok: false, failure: common };
    if (response?.status === 400) {
      const code = await errorCodeOf(response);
      if (code === "PASSWORD_TOO_SHORT") return { ok: false, failure: "password_too_short" };
      if (code === "PASSWORD_TOO_LONG") return { ok: false, failure: "password_too_long" };
      // INVALID_TOKEN: link scaduto (vale un'ora), già usato o incompleto; USER_NOT_FOUND: identità rimossa.
      if (code === "INVALID_TOKEN" || code === "USER_NOT_FOUND") return { ok: false, failure: "invalid_link" };
    }
    return { ok: false, failure: "unexpected" };
  },

  /**
   * «Cambia la password» in «Il tuo account». Le altre sessioni della persona si chiudono (un'altra scheda
   * sullo stesso browser condivide il cookie e resta dentro: Better Auth rinnova la sessione di chi cambia).
   */
  async changePassword(input: { currentPassword: string; newPassword: string }): Promise<AuthResult> {
    const response = await post("/change-password", {
      newPassword: input.newPassword,
      currentPassword: input.currentPassword,
      revokeOtherSessions: true,
    });
    if (response?.ok) return { ok: true };
    const common = commonFailure(response);
    if (common) return { ok: false, failure: common };
    // Senza una sessione valida Better Auth risponde 401 prima di guardare le password.
    if (response?.status === 401) return { ok: false, failure: "session_expired" };
    if (response?.status === 400) {
      const code = await errorCodeOf(response);
      if (code === "INVALID_PASSWORD") return { ok: false, failure: "wrong_password" };
      if (code === "PASSWORD_TOO_SHORT") return { ok: false, failure: "password_too_short" };
      if (code === "PASSWORD_TOO_LONG") return { ok: false, failure: "password_too_long" };
    }
    return { ok: false, failure: "unexpected" };
  },
};
